import { ConvertHelper } from '@barthauer/abc/core';
import { DataRowInfo, NetworkTraceModel, ObjectKey } from '@barthauer/abc/core/data';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import { GridColumnDataType } from '@barthauer/abc/ui-components';
import { MapInfoGeometryDefinitionViewModel, MapInfoObjectTypeViewModel } from '../viewModels/mapsData.viewModel';

export class NamedObjectInfo {
  id: string;
  objectTypeName: string;
}

export class MapInfo {
  IsReadOnly: string;
  Name: string;
  ObjectTypes: MapInfoObjectType[];
  DataSets: MapInfoDataSet[];
  constructor(data: any) {
    Object.assign(this, data);

    this.ObjectTypes = new Array<MapInfoObjectType>();

    if (Array.isArray(data.ObjectTypes)) {
      for (let objectType of data.ObjectTypes) {
        this.ObjectTypes.push(new MapInfoObjectType(objectType));
      }
    }
  }
}

export class MapInfoObjectType {
  Name: string;
  IdentityColumnName: string;
  DisplayName: string;
  DataSourceId: string;
  Columns: MapInfoObjectTypeColumn[];
  Commands: MapInfoCommand[];
  ObjectSets: MapInfoObjectType[];
  Templates: MapInfoLayerTemplate[];
  Points: MapInfoGeometryDefinition[];
  Lines: MapInfoGeometryDefinition[];
  Polygons: MapInfoGeometryDefinition[];
  constructor(data: any) {
    Object.assign(this, data);

    this.Columns = new Array<MapInfoObjectTypeColumn>();
    if (Array.isArray(data.Columns)) {
      for (const column of data.Columns) {
        this.Columns.push(new MapInfoObjectTypeColumn(column));
      }
    }

    this.Commands = new Array<MapInfoCommand>();
    if (Array.isArray(data.Commands)) {
      for (const command of data.Commands) {
        this.Commands.push(new MapInfoCommand(command));
      }
    }

    this.ObjectSets = new Array<MapInfoObjectType>();
    if (Array.isArray(data.ObjectSets)) {
      for (const objectSet of data.ObjectSets) {
        this.ObjectSets.push(new MapInfoObjectType(objectSet));
      }
    }

    this.Templates = new Array<MapInfoObjectType>();
    if (Array.isArray(data.Templates)) {
      for (const template of data.Templates) {
        this.Templates.push(new MapInfoLayerTemplate(template));
      }
    }

    this.Points = new Array<MapInfoGeometryDefinition>();
    if (Array.isArray(data.Points)) {
      for (const g of data.Points) {
        this.Points.push(new MapInfoGeometryDefinition(g));
      }
    }

    this.Lines = new Array<MapInfoGeometryDefinition>();
    if (Array.isArray(data.Lines)) {
      for (const g of data.Lines) {
        this.Lines.push(new MapInfoGeometryDefinition(g));
      }
    }

    this.Polygons = new Array<MapInfoGeometryDefinition>();
    if (Array.isArray(data.Polygons)) {
      for (const g of data.Polygons) {
        this.Polygons.push(new MapInfoGeometryDefinition(g));
      }
    }
  }
}

export class MapInfoDataSet {
  Name: string;
  IdentityColumnName: string;
  Columns: MapInfoObjectTypeColumn[];
  Commands: MapInfoCommand[];
  constructor(data: any) {
    Object.assign(this, data);

    this.Columns = new Array<MapInfoObjectTypeColumn>();
    if (data.Columns) {
      for (const column of data.Columns) {
        this.Columns.push(new MapInfoObjectTypeColumn(column));
      }
    }
  }
}

export class MapInfoObjectTypeColumn {
  Name: string;
  DisplayName: string;
  DataType: GridColumnDataType;
  EditMask?: string;
  SortOrder?: string;
  constructor(data: any) {
    Object.assign(this, data);
  }
}

export class MapInfoGeometryDefinition {
  Identifier: string;
  CanAdd: boolean;
  constructor(data: any) {
    Object.assign(this, data);
    this.CanAdd = ConvertHelper.toBoolean(data.CanAdd);
  }
}

export class MapInfoLayerTemplate {
  Name: string;

  constructor(data: any) {
    Object.assign(this, data);
  }
}

export class MapInfoCommand {
  Name: string;
  DisplayText: string;
  InputType: string;
  WindowType: string;
  UrlPattern: string;
  SvgImage: string;
  BadgeValueExpression: string;
  EnabledExpression: string;
  EnabledByBadgeValue: boolean;
  Visibility: string;
  constructor(data: any) {
    Object.assign(this, data);

    if (typeof data.EnabledByBadgeValue === 'string') {
      this.EnabledByBadgeValue = !(
        data.EnabledByBadgeValue === '' ||
        data.EnabledByBadgeValue === '0' ||
        data.EnabledByBadgeValue === 'false'
      );
    }
  }
}

export class EntityGeometry {
  public objectType: string | number;
  public id: string;
  public geometry__Json?: any;

  constructor(data: any) {
    Object.assign(this, data);
  }
}

export class MapObjectValue {
  Value: string;
  Objects: ObjectKey[];
  DataRows: DataRowInfo[];
  constructor(data: any) {
    Object.assign(this, data);

    this.Objects = new Array<ObjectKey>();
    if (data.Objects) {
      for (let key of data.Objects) {
        let newkey = new ObjectKey();
        newkey.Id = key.Id;
        newkey.ObjectType = key.ObjectType;
        this.Objects.push(newkey);
      }
    }

    this.DataRows = new Array<DataRowInfo>();
    if (data.DataRows) {
      for (let row of data.DataRows) {
        let newrow = new DataRowInfo();
        newrow.Id = row.Id;
        newrow.DataSetName = row.DataSetName;
        this.DataRows.push(newrow);
      }
    }
  }
}

export class ArenaSettings {
  defaultName: string;
  definitionId: string;
  isReadOnly: string;
  Layouts: ArenaLayout[];
  constructor(data: any) {
    Object.assign(this, data);

    this.Layouts = new Array<ArenaLayout>();
    if (data.Layouts) {
      for (const arenaLayout of data.Layouts) {
        this.Layouts.push(new ArenaLayout(arenaLayout));
      }
    }
  }
}

export class ArenaLayout {
  objectTypeName: string;
  usage: string;
  fullName: string;
  constructor(data: any) {
    Object.assign(this, data);
  }
}

export class MapActionError implements Error {
  public static readonly identifier = 'MapActionError';
  constructor(public readonly message: string, public readonly error?: any | null) {}
  public readonly name = MapActionError.identifier;
}

export class ObjectTypeWithGeoDefsCandidate {
  objectType: MapInfoObjectTypeViewModel;
  allowedGeoDefinitions: MapInfoGeometryDefinitionViewModel[];

  constructor(obj?: any) {
    this.allowedGeoDefinitions = [];

    if (obj) {
      Object.assign(this, obj);
    }
  }
}

export class CreateGeoObjectInfo {
  objectTypeName: string;
  geoIdentifier: string;

  constructor(obj?: any) {
    if (obj) {
      Object.assign(this, obj);
    }
  }
}

export class SelectOtAndGidListItem {
  concreteObjectTypeName: string;
  concreteObjectTypeDisplayTextSingular: string;
  groupObjectTypeName: string;
  groupObjectTypeDisplayTextPlural: string;
  geoIdentifiers: string[];

  constructor(obj?: any) {
    this.geoIdentifiers = [];

    if (obj) {
      Object.assign(this, obj);
    }
  }
}

export class NetworkObjectInfo {
  ObjectType: string | number;
  Id: string;
  Designation: string;

  constructor(obj?: any) {
    if (obj) {
      Object.assign(this, obj);
    }
  }
}

export class NetworkTracingInfo {
  InitialEntity: DataEntity;
  InitialObjectType: MapInfoObjectTypeViewModel;
  TracingModel: NetworkTraceModel;
}
